import type { AudioAnalysisFrame } from '../media/audio-analysis';

const OUTPUT_ACTIVE_LEVEL = 0.08;
const USER_SPEECH_LEVEL = 0.16;
const USER_SPEECH_FRAMES = 2;
const ECHO_SIMILARITY_LIMIT = 0.65;
const PLAYBACK_HISTORY_FRAMES = 12;

function spectrumSimilarity(left: number[], right: number[]): number {
  const length = Math.min(left.length, right.length);
  let product = 0;
  let leftMagnitude = 0;
  let rightMagnitude = 0;
  for (let index = 0; index < length; index += 1) {
    product += left[index] * right[index];
    leftMagnitude += left[index] ** 2;
    rightMagnitude += right[index] ** 2;
  }
  if (leftMagnitude === 0 || rightMagnitude === 0) return 0;
  return product / Math.sqrt(leftMagnitude * rightMagnitude);
}

export class RealtimeEchoGuard {
  private distinctSpeechFrames = 0;

  private bargeInOpen = false;

  private playbackHistory: number[][] = [];

  shouldTransmit(
    microphone: AudioAnalysisFrame,
    playback: AudioAnalysisFrame,
  ): boolean {
    if (playback.level < OUTPUT_ACTIVE_LEVEL) {
      this.reset();
      return true;
    }
    if (this.bargeInOpen) return true;

    this.playbackHistory.push([...playback.spectrum]);
    this.playbackHistory = this.playbackHistory.slice(-PLAYBACK_HISTORY_FRAMES);
    const similarity = Math.max(
      ...this.playbackHistory.map((spectrum) =>
        spectrumSimilarity(microphone.spectrum, spectrum),
      ),
    );
    const distinctSpeech =
      microphone.level >= USER_SPEECH_LEVEL &&
      microphone.level >= playback.level * 0.5 &&
      similarity < ECHO_SIMILARITY_LIMIT;
    this.distinctSpeechFrames = distinctSpeech
      ? this.distinctSpeechFrames + 1
      : 0;
    if (this.distinctSpeechFrames >= USER_SPEECH_FRAMES) {
      this.bargeInOpen = true;
    }
    return this.bargeInOpen;
  }

  reset(): void {
    this.distinctSpeechFrames = 0;
    this.bargeInOpen = false;
    this.playbackHistory = [];
  }
}
